import { SiteNickname } from '@unisporkal/sites';
import { FilmDefinitionSearchFilterEnum } from 'app/domain';

// One key per filter block the FilterPanelDrawer can render. The union of both
// sites' blocks — each site declares the subset it renders, and in which
// order, via `orderedFilters`.
export enum FilterKeyEnum {
  APPLIED_FILTERS = 'applied_filters',
  AGREEMENTS = 'agreements',
  SORT_BY = 'sort_by',
  COLOR_MOOD = 'color_mood',
  ONLY_FROM = 'only_from',
  ISTOCK_COLLECTION = 'istock_collection',
  ALLOWED_USE = 'allowed_use',
  DATE_RANGE = 'date_range',
  LICENSE = 'license',
  VIDEO_TEMPLATE_CATEGORY = 'video_template_category',
  VIDEO_TEMPLATE_COMPATIBILITY = 'video_template_compatibility',
  ORIENTATION = 'orientation',
  IMAGE_RESOLUTION = 'image_resolution',
  DURATION = 'duration',
  FILM_DEFINITION = 'film_definition',
  FRAME_RATE = 'frame_rate',
  RELEASE_STATUS = 'release_status',
  PEOPLE = 'people',
  NUM_PEOPLE = 'num_people',
  AGE = 'age',
  ETHNICITY = 'ethnicity',
  EVENTS = 'events',
  COMPOSITION = 'composition',
  COLOR = 'color',
  VIEW_POINT = 'view_point',
  IMAGE_TECHNIQUE = 'image_technique',
  IMAGE_SIZE = 'image_size',
  UPLOAD_DATE = 'upload_date',
  LOCATIONS = 'locations',
  ARTIST = 'artist',
  COLLECTIONS = 'collections',
  EXCLUDE_EDITORIAL_ONLY = 'exclude_editorial_only',
  EMBEDDABLE = 'embeddable',
  EXCLUDE_NUDITY = 'exclude_nudity',
  KEYWORD_MATCH = 'keyword_match',
  CLEAR_BUTTON = 'clear_button',
}

// Which shared molecule renders each option-list filter's choices.
export enum OptionListKindEnum {
  CHECKBOX = 'checkbox',
  TOGGLE = 'toggle',
}

export type FilterPanelDrawerConfig = {
  /** The filters this site renders, in render order. */
  orderedFilters: FilterKeyEnum[];
  /** Multi-select molecule injected into every option-list filter. */
  optionListKind: OptionListKindEnum;
  filmDefinition: {
    values: FilmDefinitionSearchFilterEnum[];
    headerIloxKey: string;
  };
  locationsInitiallyExpanded: boolean;
};

const gettyFilterPanelDrawerConfig: FilterPanelDrawerConfig = {
  orderedFilters: [
    FilterKeyEnum.APPLIED_FILTERS,
    FilterKeyEnum.AGREEMENTS,
    FilterKeyEnum.SORT_BY,
    FilterKeyEnum.COLOR_MOOD,
    FilterKeyEnum.DATE_RANGE,
    FilterKeyEnum.LICENSE,
    FilterKeyEnum.VIDEO_TEMPLATE_CATEGORY,
    FilterKeyEnum.VIDEO_TEMPLATE_COMPATIBILITY,
    FilterKeyEnum.ORIENTATION,
    FilterKeyEnum.IMAGE_RESOLUTION,
    FilterKeyEnum.DURATION,
    FilterKeyEnum.FILM_DEFINITION,
    FilterKeyEnum.FRAME_RATE,
    FilterKeyEnum.RELEASE_STATUS,
    FilterKeyEnum.PEOPLE,
    FilterKeyEnum.EVENTS,
    FilterKeyEnum.COMPOSITION,
    FilterKeyEnum.COLOR,
    FilterKeyEnum.VIEW_POINT,
    FilterKeyEnum.IMAGE_TECHNIQUE,
    FilterKeyEnum.LOCATIONS,
    FilterKeyEnum.ARTIST,
    FilterKeyEnum.COLLECTIONS,
    FilterKeyEnum.EXCLUDE_EDITORIAL_ONLY,
    FilterKeyEnum.EMBEDDABLE,
    FilterKeyEnum.EXCLUDE_NUDITY,
    FilterKeyEnum.KEYWORD_MATCH,
  ],
  optionListKind: OptionListKindEnum.TOGGLE,
  filmDefinition: {
    values: Object.values(FilmDefinitionSearchFilterEnum),
    headerIloxKey: 'search_filters.film_definition_heading',
  },
  locationsInitiallyExpanded: false,
};

const istockFilterPanelDrawerConfig: FilterPanelDrawerConfig = {
  orderedFilters: [
    FilterKeyEnum.SORT_BY,
    FilterKeyEnum.ONLY_FROM,
    FilterKeyEnum.ISTOCK_COLLECTION,
    FilterKeyEnum.ALLOWED_USE,
    FilterKeyEnum.VIDEO_TEMPLATE_CATEGORY,
    FilterKeyEnum.VIDEO_TEMPLATE_COMPATIBILITY,
    FilterKeyEnum.ORIENTATION,
    FilterKeyEnum.DURATION,
    FilterKeyEnum.FILM_DEFINITION,
    FilterKeyEnum.FRAME_RATE,
    FilterKeyEnum.LOCATIONS,
    FilterKeyEnum.NUM_PEOPLE,
    FilterKeyEnum.AGE,
    FilterKeyEnum.ETHNICITY,
    FilterKeyEnum.COLOR,
    FilterKeyEnum.IMAGE_SIZE,
    FilterKeyEnum.UPLOAD_DATE,
    FilterKeyEnum.EXCLUDE_NUDITY,
    FilterKeyEnum.KEYWORD_MATCH,
    FilterKeyEnum.CLEAR_BUTTON,
  ],
  optionListKind: OptionListKindEnum.CHECKBOX,
  filmDefinition: {
    values: [
      FilmDefinitionSearchFilterEnum.FOUR_K,
      FilmDefinitionSearchFilterEnum.HD,
    ],
    // "defintition" is misspelled in the live translation key — do not correct it here.
    headerIloxKey: 'search_filters.film_defintition_filter_heading',
  },
  locationsInitiallyExpanded: true,
};

const filterPanelDrawerConfigRegistry: Record<
  SiteNickname,
  FilterPanelDrawerConfig
> = {
  getty: gettyFilterPanelDrawerConfig,
  istock: istockFilterPanelDrawerConfig,
};

export const getFilterPanelDrawerConfig = (
  siteName: SiteNickname
): FilterPanelDrawerConfig =>
  filterPanelDrawerConfigRegistry[siteName] ??
  filterPanelDrawerConfigRegistry.getty;
